/**
 * Conciliation Store
 * 
 * Zustand store with persistence for the Conciliation job workflow.
 * Manages jobs, documents, sample previews, and QA gate tracking.
 * 
 * TODO [PHASE 2]: Replace localStorage persistence with Supabase real-time sync
 * 
 * @module features/conciliation/store/conciliationStore
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AUTH_STORAGE_KEY, isSessionOnlyMode } from '@/integrations/supabase/rememberMeStorage';
import type {
  ConciliationJob,
  ConciliationConfig,
  DocumentRow,
  SampleGroupPreview,
  JobStatus,
  DocumentStatus,
  ApprovalStatus,
} from '../types/conciliation.types';
import { logger } from '@/lib/logging';

// ============= Store State Interface =============

interface ConciliationState {
  // Current configuration
  currentConfig: ConciliationConfig;
  
  // Uploaded CSV file reference (for later server upload)
  csvFile: File | null;
  
  // Jobs list
  jobs: ConciliationJob[];
  
  // Documents for all jobs
  documents: DocumentRow[];
  
  // Sample previews for current upload
  samplePreviews: SampleGroupPreview[];
  
  // Track which groups have been previewed (QA gate)
  previewedGroups: Set<string>;
  
  // Current active job ID
  currentJobId: string | null;

  // Internal org/business tracking (not exposed to consumers)
  _orgId: string | null;
  _businessId: string | null;

  // ============= Actions =============

  // Org/business scoping
  setOrgId: (orgId: string) => void;
  setBusinessId: (businessId: string) => void;
  clearForOrgSwitch: () => void;
  
  // Config
  setConfig: (config: Partial<ConciliationConfig>) => void;
  resetConfig: () => void;
  
  // CSV File
  setCsvFile: (file: File | null) => void;
  
  // Jobs
  createJob: (name: string, totalRows: number) => ConciliationJob;
  updateJob: (jobId: string, updates: Partial<ConciliationJob>) => void;
  updateJobStatus: (jobId: string, status: JobStatus) => void;
  deleteJob: (jobId: string) => void;
  getJobById: (jobId: string) => ConciliationJob | undefined;
  
  // Documents
  addDocuments: (documents: DocumentRow[]) => void;
  updateDocument: (docId: string, updates: Partial<DocumentRow>) => void;
  updateDocumentStatus: (docId: string, status: DocumentStatus) => void;
  approveDocument: (docId: string) => void;
  rejectDocument: (docId: string) => void;
  approveDocuments: (docIds: string[]) => void;
  rejectDocuments: (docIds: string[]) => void;
  approveAllDocuments: (jobId: string) => void;
  getDocumentsByJob: (jobId: string) => DocumentRow[];
  
  // Sample Previews
  setSamplePreviews: (previews: SampleGroupPreview[]) => void;
  clearSamplePreviews: () => void;
  
  // QA Gate
  markGroupPreviewed: (groupKey: string) => void;
  isGroupPreviewed: (groupKey: string) => boolean;
  areAllGroupsPreviewed: () => boolean;
  clearPreviewedGroups: () => void;
  resetPreviewedGroups: () => void;
  
  // Current Job
  setCurrentJobId: (jobId: string | null) => void;
  
  // Reset
  reset: () => void;
}

// ============= Default Config =============

const defaultConfig: ConciliationConfig = {
  courtId: '',
  templateFamilyId: '',
  templateVariantId: '',
  language: 'EN',
  templateSelectorColumn: 'Template_ID',
  groupingColumns: ['Template_ID', 'Bank_Name'],
  emailColumn: 'Bank_Email',
  emailSubjectTemplate: 'Conciliation Notice - {{Template_ID}} - {{Bank_Name}}',
  emailBodyTemplate: 'Dear Sir/Madam,\n\nPlease find attached the conciliation documents for your reference.\n\nRegards,\n{{Organization_Name}}',
  zipNameTemplate: '{{Template_ID}}_{{Bank_Name}}_{{Date}}.zip',
};

// ============= Initial State =============

const initialState = {
  currentConfig: defaultConfig,
  csvFile: null as File | null,
  jobs: [] as ConciliationJob[],
  documents: [] as DocumentRow[],
  samplePreviews: [] as SampleGroupPreview[],
  previewedGroups: new Set<string>(),
  currentJobId: null as string | null,
  _orgId: null as string | null,
  _businessId: null as string | null,
};

// ============= Store Creation =============

export const useConciliationStore = create<ConciliationState>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      // ============= Org Scoping Actions =============

      setOrgId: (orgId) => {
        const current = get()._orgId;
        if (current && current !== orgId) {
          logger.info('CONCILIATION_STORE_ORG_SWITCH', { from: current, to: orgId });
          set({
            ...initialState,
            _orgId: orgId,
            _businessId: null,
            previewedGroups: new Set(),
          });
        } else if (!current) {
          set({ _orgId: orgId });
        }
      },

      setBusinessId: (businessId) => {
        const current = get()._businessId;
        if (current && current !== businessId) {
          logger.info('CONCILIATION_STORE_BUSINESS_SWITCH', { from: current, to: businessId });
          set({
            ...initialState,
            _orgId: get()._orgId,
            _businessId: businessId,
            previewedGroups: new Set(),
          });
        } else if (!current) {
          set({ _businessId: businessId });
        }
      },

      clearForOrgSwitch: () => {
        logger.info('CONCILIATION_STORE_CLEAR_ORG_SWITCH');
        set({
          ...initialState,
          _businessId: null,
          previewedGroups: new Set(),
        });
      },

      // ============= Config Actions =============
      
      setConfig: (config) => {
        set((state) => ({
          currentConfig: { ...state.currentConfig, ...config },
        }));
        logger.debug('CONCILIATION_CONFIG_UPDATED', { config });
      },
      
      resetConfig: () => {
        set({ currentConfig: defaultConfig });
        logger.debug('CONCILIATION_CONFIG_RESET');
      },
      
      setCsvFile: (file) => {
        set({ csvFile: file });
        logger.debug('CONCILIATION_CSV_FILE_SET', { fileName: file?.name ?? null });
      },
      
      // ============= Job Actions =============
      
      createJob: (name, totalRows) => {
        const newJob: ConciliationJob = {
          id: crypto.randomUUID(),
          name,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          status: 'QUEUED',
          config: get().currentConfig,
          totalRows,
          generatedCount: 0,
          failedCount: 0,
          approvedCount: 0,
          rejectedCount: 0,
          emailedCount: 0,
          progressPercent: 0,
        };
        
        set((state) => ({
          jobs: [newJob, ...state.jobs],
          currentJobId: newJob.id,
        }));
        
        logger.info('CONCILIATION_JOB_CREATED', { 
          jobId: newJob.id, 
          name, 
          totalRows 
        });
        
        return newJob;
      },
      
      updateJob: (jobId, updates) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === jobId
              ? { ...job, ...updates, updatedAt: new Date().toISOString() }
              : job
          ),
        }));
        logger.debug('CONCILIATION_JOB_UPDATED', { jobId, updates });
      },
      
      updateJobStatus: (jobId, status) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === jobId
              ? { ...job, status, updatedAt: new Date().toISOString() }
              : job
          ),
        }));
        logger.info('CONCILIATION_JOB_STATUS_CHANGED', { jobId, status });
      },
      
      deleteJob: (jobId) => {
        set((state) => ({
          jobs: state.jobs.filter((job) => job.id !== jobId),
          documents: state.documents.filter((doc) => doc.jobId !== jobId),
          currentJobId: state.currentJobId === jobId ? null : state.currentJobId,
        }));
        logger.info('CONCILIATION_JOB_DELETED', { jobId });
      },
      
      getJobById: (jobId) => {
        return get().jobs.find((job) => job.id === jobId);
      },
      
      // ============= Document Actions =============
      
      addDocuments: (documents) => {
        set((state) => ({
          documents: [...state.documents, ...documents],
        }));
        logger.info('CONCILIATION_DOCUMENTS_ADDED', { count: documents.length });
      },
      
      updateDocument: (docId, updates) => {
        set((state) => ({
          documents: state.documents.map((doc) =>
            doc.id === docId ? { ...doc, ...updates } : doc
          ),
        }));
      },
      
      updateDocumentStatus: (docId, status) => {
        set((state) => ({
          documents: state.documents.map((doc) =>
            doc.id === docId ? { ...doc, status } : doc
          ),
        }));
      },
      
      approveDocument: (docId) => {
        const state = get();
        const doc = state.documents.find((d) => d.id === docId);
        if (!doc) return;
        
        set((s) => ({
          documents: s.documents.map((d) =>
            d.id === docId
              ? { ...d, approvalStatus: 'APPROVED' as ApprovalStatus, approvedAt: new Date().toISOString() }
              : d
          ),
        }));
        
        // Update job counts
        const job = state.jobs.find((j) => j.id === doc.jobId);
        if (job) {
          const wasRejected = doc.approvalStatus === 'REJECTED';
          get().updateJob(doc.jobId, {
            approvedCount: job.approvedCount + 1,
            rejectedCount: wasRejected ? job.rejectedCount - 1 : job.rejectedCount,
          });
        }
        
        logger.debug('CONCILIATION_DOCUMENT_APPROVED', { docId });
      },
      
      rejectDocument: (docId) => {
        const state = get();
        const doc = state.documents.find((d) => d.id === docId);
        if (!doc) return;
        
        set((s) => ({
          documents: s.documents.map((d) =>
            d.id === docId
              ? { ...d, approvalStatus: 'REJECTED' as ApprovalStatus }
              : d
          ),
        }));
        
        // Update job counts
        const job = state.jobs.find((j) => j.id === doc.jobId);
        if (job) {
          const wasApproved = doc.approvalStatus === 'APPROVED';
          get().updateJob(doc.jobId, {
            rejectedCount: job.rejectedCount + 1,
            approvedCount: wasApproved ? job.approvedCount - 1 : job.approvedCount,
          });
        }
        
        logger.debug('CONCILIATION_DOCUMENT_REJECTED', { docId });
      },
      
      approveDocuments: (docIds) => {
        docIds.forEach((id) => get().approveDocument(id));
        logger.info('CONCILIATION_DOCUMENTS_BULK_APPROVED', { count: docIds.length });
      },
      
      rejectDocuments: (docIds) => {
        docIds.forEach((id) => get().rejectDocument(id));
        logger.info('CONCILIATION_DOCUMENTS_BULK_REJECTED', { count: docIds.length });
      },
      
      approveAllDocuments: (jobId) => {
        const docs = get().documents.filter(
          (d) => d.jobId === jobId && d.approvalStatus === 'PENDING' && d.status === 'GENERATED'
        );
        docs.forEach((d) => get().approveDocument(d.id));
        logger.info('CONCILIATION_DOCUMENTS_ALL_APPROVED', { jobId, count: docs.length });
      },
      
      getDocumentsByJob: (jobId) => {
        return get().documents.filter((doc) => doc.jobId === jobId);
      },
      
      // ============= Sample Preview Actions =============
      
      setSamplePreviews: (previews) => {
        set({ samplePreviews: previews });
        logger.debug('CONCILIATION_SAMPLE_PREVIEWS_SET', { count: previews.length });
      },
      
      clearSamplePreviews: () => {
        set({ samplePreviews: [], previewedGroups: new Set() });
      },
      
      // ============= QA Gate Actions =============
      
      markGroupPreviewed: (groupKey) => {
        set((state) => ({
          previewedGroups: new Set([...state.previewedGroups, groupKey]),
        }));
        logger.debug('CONCILIATION_GROUP_PREVIEWED', { groupKey });
      },
      
      isGroupPreviewed: (groupKey) => {
        return get().previewedGroups.has(groupKey);
      },
      
      areAllGroupsPreviewed: () => {
        const { samplePreviews, previewedGroups } = get();
        if (samplePreviews.length === 0) return false;
        return samplePreviews.every((p) => previewedGroups.has(p.groupKey));
      },
      
      clearPreviewedGroups: () => {
        set({ previewedGroups: new Set() });
      },
      
      // Alias for resetPreviewedGroups
      resetPreviewedGroups: () => {
        set({ previewedGroups: new Set() });
      },
      
      // ============= Current Job Actions =============
      
      setCurrentJobId: (jobId) => {
        set({ currentJobId: jobId });
      },
      
      // ============= Reset =============
      
      reset: () => {
        set({
          ...initialState,
          jobs: [],
        });
        logger.info('CONCILIATION_STORE_RESET');
      },
    }),
    {
      name: 'conciliation-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        jobs: state.jobs,
        documents: state.documents,
        currentJobId: state.currentJobId,
        currentConfig: state.currentConfig,
        _orgId: state._orgId,
        _businessId: state._businessId,
        // Don't persist: samplePreviews, previewedGroups, csvFile
      }),
      // Handle Set serialization + validate org context on rehydration
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.previewedGroups = new Set();
          // Validate stored org matches current auth session to prevent cross-tenant data flash
          try {
            const storage = isSessionOnlyMode() ? sessionStorage : localStorage;
            const sessionRaw = storage.getItem(AUTH_STORAGE_KEY);
            if (sessionRaw && state._orgId) {
              const session = JSON.parse(sessionRaw);
              const currentOrgId = session?.user?.app_metadata?.org_id;
              if (currentOrgId && currentOrgId !== state._orgId) {
                logger.info('CONCILIATION_STORE_REHYDRATE_ORG_MISMATCH', {
                  storeOrg: state._orgId, sessionOrg: currentOrgId,
                });
                Object.assign(state, { ...initialState, previewedGroups: new Set() });
                return;
              }
            }
          } catch {
            // If session parsing fails, don't block rehydration
          }
          if (state._orgId) {
            logger.info('CONCILIATION_STORE_REHYDRATED', { orgId: state._orgId, jobCount: state.jobs.length });
          } else {
            logger.debug('CONCILIATION_STORE_REHYDRATED_NO_ORG');
          }
        }
      },
    }
  )
);

// ============= Selectors =============

export const selectCurrentJob = (state: ConciliationState) =>
  state.currentJobId ? state.jobs.find((j) => j.id === state.currentJobId) : null;

export const selectJobsCount = (state: ConciliationState) => state.jobs.length;

export const selectRunningJobsCount = (state: ConciliationState) =>
  state.jobs.filter((j) => 
    ['QUEUED', 'PROCESSING'].includes(j.status)
  ).length;

export const selectCompletedThisWeek = (state: ConciliationState) => {
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  return state.jobs.filter(
    (j) => j.status === 'COMPLETED' && new Date(j.updatedAt) >= weekAgo
  ).length;
};

export const selectTotalDocumentsGenerated = (state: ConciliationState) =>
  state.jobs.reduce((sum, j) => sum + j.generatedCount, 0);

export const selectTotalEmailsSent = (state: ConciliationState) =>
  state.jobs.reduce((sum, j) => sum + j.emailedCount, 0);
